import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import ReactDOM from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Lock, ArrowLeft, ShieldAlert, ShieldCheck, Package, ExternalLink, Minus, Plus, Beaker, AlertTriangle, Activity, Clock, FileCheck2, Calculator } from "lucide-react";
import { getRestrictedToken, setRestrictedToken } from "@/lib/api";
import { findRestrictedProduct } from "@/lib/restrictedCatalog";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const MoleculeViewer = lazy(() => import("@/components/MoleculeViewer").then((m) => ({ default: m.MoleculeViewer })));

const CRIMSON = "#C8102E";
const CRIMSON_DIM = "#8a0a1f";
const CRIMSON_BORDER = "#3a1418";

// Same tiered bundle pricing as the main catalog, just rebranded.
const BUNDLE_TIERS = [
    { qty: 3, pct: 0.10, label: "3-VIAL MIX", desc: "10% OFF · Mix & match any compound" },
    { qty: 5, pct: 0.15, label: "5-VIAL STACK", desc: "15% OFF · Most-studied research stack" },
    { qty: 10, pct: 0.20, label: "10-PACK BULK", desc: "20% OFF · Maximum research economy" },
];

const tierFor = (q) => {
    let t = null;
    for (const tier of BUNDLE_TIERS) if (q >= tier.qty) t = tier;
    return t;
};

const QuickFact = ({ label, value, icon: Icon }) => (
    <div className="border-l-2 pl-3 py-1" style={{ borderColor: CRIMSON_DIM }}>
        <div className="overline text-[9px] tracking-[0.18em] flex items-center gap-1.5" style={{ color: CRIMSON }}>
            {Icon && <Icon className="w-3 h-3" strokeWidth={2} />} {label}
        </div>
        <div className="mt-1 text-sm text-ink leading-snug">{value}</div>
    </div>
);

const Section = ({ num, label, children }) => (
    <section className="border-t pt-10" style={{ borderColor: CRIMSON_BORDER }}>
        <div className="flex items-baseline gap-3 mb-4">
            <span className="font-mono text-[10px] tracking-[0.22em]" style={{ color: CRIMSON }}>/ {num}</span>
            <p className="overline text-[10px] tracking-[0.22em]" style={{ color: CRIMSON }}>{label}</p>
        </div>
        <div className="prose-restricted text-ink-dim leading-relaxed text-[15px]">{children}</div>
    </section>
);

// Inline dose calculator tailored to AAS volumetric dosing (mg/mL → mL/injection)
function AasDoseCalc({ product }) {
    const totalMg = (product.mg_per_ml || 0) * (product.vial_ml || 0);
    const defaultDoseMg = product.mg_per_ml === 100 ? 75 : (product.mg_per_ml === 250 ? 250 : 300);
    const defaultFreqDays = product.mg_per_ml === 100 ? 2 : 3.5;
    const [dose, setDose] = useState(defaultDoseMg);
    const [freq, setFreq] = useState(defaultFreqDays);
    const [vials, setVials] = useState(1);

    const calc = useMemo(() => {
        const d = parseFloat(dose);
        if (!d || d <= 0) return null;
        const mL = d / product.mg_per_ml;
        const totalMgOwned = totalMg * vials;
        const dosesAvailable = totalMgOwned / d;
        const daysSupply = dosesAvailable * freq;
        const weeksSupply = daysSupply / 7;
        const weeklyMg = (d / freq) * 7;
        return { mL, dosesAvailable, daysSupply, weeksSupply, weeklyMg };
    }, [dose, freq, vials, product.mg_per_ml, totalMg]);

    if (!product.mg_per_ml) return null;

    return (
        <div className="border bg-ink-surface p-6" style={{ borderColor: CRIMSON_BORDER }} data-testid="restricted-dose-calc">
            <div className="flex items-center gap-2 mb-1">
                <Calculator className="w-4 h-4" style={{ color: CRIMSON }} strokeWidth={2} />
                <p className="overline text-[10px] tracking-[0.2em]" style={{ color: CRIMSON }}>/ DOSE CALCULATOR</p>
            </div>
            <p className="text-xs text-ink-dim mt-1 mb-5">{product.mg_per_ml}mg per millilitre · {product.vial_ml}mL vial = {totalMg}mg total per vial.</p>

            <div className="grid sm:grid-cols-3 gap-4">
                <label className="block">
                    <span className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1.5 block">DOSE PER INJECTION</span>
                    <div className="relative">
                        <input type="number" step="5" min="0" value={dose} onChange={(e) => setDose(e.target.value)}
                            className="w-full bg-ink-base border text-ink px-3 py-2.5 font-mono text-sm outline-none focus:border-[#C8102E]"
                            style={{ borderColor: CRIMSON_BORDER }} data-testid="dose-mg-input" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-ink-mute">MG</span>
                    </div>
                </label>
                <label className="block">
                    <span className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1.5 block">INJECTION FREQUENCY</span>
                    <select value={freq} onChange={(e) => setFreq(parseFloat(e.target.value))}
                        className="w-full bg-ink-base border text-ink px-3 py-2.5 text-sm outline-none focus:border-[#C8102E]"
                        style={{ borderColor: CRIMSON_BORDER }} data-testid="dose-freq-input">
                        <option value={1}>Every day</option>
                        <option value={2}>Every other day</option>
                        <option value={3.5}>Twice per week</option>
                        <option value={7}>Once per week</option>
                    </select>
                </label>
                <label className="block">
                    <span className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1.5 block">VIALS IN STUDY</span>
                    <div className="inline-flex items-center border w-full" style={{ borderColor: CRIMSON_BORDER }}>
                        <button onClick={() => setVials(Math.max(1, vials - 1))} className="px-3 py-2.5 text-ink-dim hover:text-ink">
                            <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="flex-1 text-center font-mono text-sm">{vials}</span>
                        <button onClick={() => setVials(vials + 1)} className="px-3 py-2.5 text-ink-dim hover:text-ink">
                            <Plus className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </label>
            </div>

            {calc && (
                <div className="mt-5 grid sm:grid-cols-4 gap-3 pt-5 border-t" style={{ borderColor: CRIMSON_BORDER }}>
                    <div>
                        <div className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1">VOLUME PER SHOT</div>
                        <div className="font-mono text-xl font-bold" style={{ color: CRIMSON }} data-testid="calc-volume">{calc.mL.toFixed(2)} <span className="text-xs text-ink-mute">mL</span></div>
                    </div>
                    <div>
                        <div className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1">WEEKLY DOSE</div>
                        <div className="font-mono text-xl font-bold text-ink">{Math.round(calc.weeklyMg)} <span className="text-xs text-ink-mute">mg/wk</span></div>
                    </div>
                    <div>
                        <div className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1">DOSES AVAILABLE</div>
                        <div className="font-mono text-xl font-bold text-ink">{Math.floor(calc.dosesAvailable)}</div>
                    </div>
                    <div>
                        <div className="overline text-[9px] tracking-[0.18em] text-ink-mute mb-1">RESEARCH WINDOW</div>
                        <div className="font-mono text-xl font-bold text-ink">{calc.weeksSupply.toFixed(1)} <span className="text-xs text-ink-mute">wks</span></div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function RestrictedProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { addToCart, formatPrice, cart } = useApp();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [loading, setLoading] = useState(true);
    const [coaOpen, setCoaOpen] = useState(false);

    useEffect(() => {
        if (!getRestrictedToken()) {
            navigate("/restricted/login");
            return;
        }
        setLoading(true);
        let cancelled = false;
        findRestrictedProduct(slug)
            .then(({ product: p, source }) => {
                if (cancelled) return;
                if (!p) {
                    toast.error("Product not found");
                    navigate("/restricted/shop");
                    return;
                }
                setProduct(p);
                setLoading(false);
                if (source === "fallback") {
                    toast.message("Showing cached version · live data unavailable", { duration: 2500 });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setLoading(false);
                if (err?.response?.status === 401) {
                    setRestrictedToken(null);
                    navigate("/restricted/login");
                } else {
                    toast.error("Failed to load product");
                }
            });
        window.scrollTo({ top: 0 });
        return () => { cancelled = true; };
    }, [slug, navigate]);

    // Live total + bundle tier — combine ALL restricted items in cart for tier qualification
    const restrictedCartQty = useMemo(() => cart.filter((i) => i.product_id?.startsWith("r-")).reduce((s, i) => s + i.quantity, 0), [cart]);
    const projectedQty = restrictedCartQty + qty;
    const activeTier = tierFor(projectedQty);
    const subtotalUsd = (product?.price_usd || 0) * qty;
    const discountUsd = activeTier ? subtotalUsd * activeTier.pct : 0;
    const finalUsd = subtotalUsd - discountUsd;

    const handleAdd = () => {
        if (!product) return;
        addToCart({
            id: product.id,
            slug: product.slug,
            name: product.name,
            dosage: product.dosage,
            price_usd: product.price_usd,
            image: product.front_image || product.image,
            molecule: product.molecule,
            region: product.region,
        }, qty);
        toast.success(`${qty} × ${product.name} added to cart`);
    };

    if (loading) {
        return <div className="min-h-[60vh] flex items-center justify-center bg-ink-base text-ink-mute font-mono text-sm">LOADING RESEARCH DOSSIER…</div>;
    }
    if (!product) return null;

    return (
        <div className="min-h-screen bg-ink-base text-ink" data-testid="restricted-product-page">
            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 md:px-12 py-10">

                {/* Breadcrumb */}
                <nav className="mb-8 text-xs font-mono uppercase tracking-[0.15em] text-ink-mute">
                    <Link to="/restricted/shop" className="hover:text-ink inline-flex items-center gap-2" data-testid="restricted-back-shop">
                        <ArrowLeft className="w-3 h-3" /> CATALOG
                    </Link>
                    <span className="mx-2 text-ink-mute/40">/</span>
                    <span style={{ color: CRIMSON }}>RESTRICTED</span>
                    <span className="mx-2 text-ink-mute/40">/</span>
                    <span className="text-ink-dim">{product.name}</span>
                </nav>

                {/* HERO */}
                <div className="grid md:grid-cols-[1.05fr_1fr] gap-10 mb-16">
                    {/* Image + 3D viewer column */}
                    <div className="space-y-4">
                        <div className="border" style={{ borderColor: CRIMSON_BORDER }}>
                            <div className="aspect-[4/3] overflow-hidden bg-ink-surface">
                                <img src={product.front_image || product.image} alt={product.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-3 border-t flex items-center gap-2" style={{ borderColor: CRIMSON_BORDER }}>
                                <Lock className="w-3.5 h-3.5" style={{ color: CRIMSON }} strokeWidth={2} />
                                <p className="overline text-[10px] tracking-[0.18em]" style={{ color: CRIMSON }}>/ RESTRICTED · MEMBERS ONLY</p>
                            </div>
                        </div>

                        {/* 3D molecule viewer (lazy) */}
                        {product.molecule && (
                            <div className="border bg-ink-surface" style={{ borderColor: CRIMSON_BORDER }} data-testid="restricted-3d-viewer">
                                <div className="p-3 border-b flex items-center justify-between gap-2" style={{ borderColor: CRIMSON_BORDER }}>
                                    <div className="flex items-center gap-2">
                                        <Activity className="w-3.5 h-3.5" style={{ color: CRIMSON }} strokeWidth={2} />
                                        <p className="overline text-[10px] tracking-[0.18em]" style={{ color: CRIMSON }}>/ 3D MOLECULAR STRUCTURE</p>
                                    </div>
                                    <span className="text-[9px] font-mono text-ink-mute">ROTATE · ZOOM</span>
                                </div>
                                <div className="h-[300px]">
                                    <Suspense fallback={<div className="h-full flex items-center justify-center text-ink-mute font-mono text-xs">LOADING 3D MODEL…</div>}>
                                        <MoleculeViewer molecule={product.molecule} className="w-full h-full" />
                                    </Suspense>
                                </div>
                                <div className="p-3 border-t flex flex-wrap gap-2 text-[10px] font-mono text-ink-mute" style={{ borderColor: CRIMSON_BORDER }}>
                                    <span>FORMULA: <span className="text-ink">{product.chemical_formula || "—"}</span></span>
                                    {product.molar_mass_g_mol && <span>· MW: <span className="text-ink">{product.molar_mass_g_mol} g/mol</span></span>}
                                    <span>· PUBCHEM: <span className="text-ink">{product.molecule.id}</span></span>
                                </div>
                            </div>
                        )}

                        {/* Certificate of Analysis — inline image card (only when admin has uploaded one) */}
                        {product.coa_image_url && (
                            <div className="border bg-ink-surface" style={{ borderColor: CRIMSON_BORDER }} data-testid="restricted-coa-card">
                                <div className="p-3 border-b flex items-center justify-between gap-2" style={{ borderColor: CRIMSON_BORDER }}>
                                    <div className="flex items-center gap-2">
                                        <FileCheck2 className="w-3.5 h-3.5" style={{ color: CRIMSON }} strokeWidth={2} />
                                        <p className="overline text-[10px] tracking-[0.18em]" style={{ color: CRIMSON }}>/ CERTIFICATE OF ANALYSIS · JANOSHIK</p>
                                    </div>
                                    <span className="text-[9px] font-mono text-ink-mute">CLICK TO ENLARGE</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setCoaOpen(true)}
                                    className="block w-full bg-ink-base p-3 hover:bg-ink-surface/60 transition-colors group text-left"
                                    aria-label="Open Certificate of Analysis"
                                    data-testid="restricted-coa-image-tile"
                                >
                                    <div className="border" style={{ borderColor: CRIMSON_BORDER }}>
                                        <img
                                            src={product.coa_image_url}
                                            alt={`${product.name} — Janoshik Certificate of Analysis`}
                                            className="w-full h-auto max-h-[440px] object-contain bg-white"
                                            loading="lazy"
                                        />
                                    </div>
                                </button>
                                <div className="p-3 border-t flex items-center justify-between text-[10px] font-mono" style={{ borderColor: CRIMSON_BORDER }}>
                                    <span className="text-ink-mute">HPLC purity · MS identity · Independently tested</span>
                                    <button
                                        type="button"
                                        onClick={() => setCoaOpen(true)}
                                        className="inline-flex items-center gap-1.5 uppercase tracking-[0.15em] hover:underline"
                                        style={{ color: CRIMSON }}
                                        data-testid="restricted-coa-view-full"
                                    >
                                        VIEW FULL SIZE <ExternalLink className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Buy + meta column */}
                    <div>
                        <p className="overline text-[11px] tracking-[0.22em]" style={{ color: CRIMSON }}>/ {product.category} · MEMBERS-ONLY</p>
                        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-ink leading-[0.96] tracking-tight">{product.name}</h1>
                        <p className="mt-3 text-sm font-mono text-ink-dim">{product.dosage}</p>

                        {/* Trust row */}
                        <div className="mt-6 flex flex-wrap gap-2">
                            <a href={product.coa_url || "#"} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => { if (product.coa_image_url || !product.coa_url || product.coa_url === "https://janoshik.com/") { e.preventDefault(); setCoaOpen(true); } }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-ink-surface text-[11px] font-bold font-mono uppercase tracking-wider border hover:border-[#C8102E] transition-colors"
                                style={{ borderColor: CRIMSON_BORDER, color: CRIMSON }}
                                data-testid="restricted-coa-badge">
                                <ShieldCheck className="w-3.5 h-3.5" /> JANOSHIK COA <ExternalLink className="w-3 h-3" />
                            </a>
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-ink-surface text-ink-dim text-[11px] font-bold font-mono uppercase tracking-wider border" style={{ borderColor: CRIMSON_BORDER }}>
                                <Package className="w-3.5 h-3.5" /> DISCREET SHIPPING
                            </span>
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-ink-surface text-gold text-[11px] font-bold font-mono uppercase tracking-wider border border-gold/30">
                                🇨🇦 CANADIAN WAREHOUSE
                            </span>
                        </div>

                        <p className="mt-6 text-base text-ink-dim leading-relaxed">{product.short}</p>

                        {/* Price block */}
                        <div className="mt-8 py-6 border-y" style={{ borderColor: CRIMSON_BORDER }}>
                            <div className="flex items-end justify-between flex-wrap gap-4">
                                <div>
                                    <p className="overline text-[10px] text-ink-mute mb-2">UNIT PRICE</p>
                                    <div className="flex items-baseline gap-3">
                                        <p className="font-display text-4xl font-bold text-ink" data-testid="restricted-product-price">{formatPrice(product.price_usd)}</p>
                                        <p className="text-sm font-mono text-ink-mute">per vial</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="overline text-[10px] text-ink-mute mb-2">QUANTITY</p>
                                    <div className="inline-flex items-center border" style={{ borderColor: CRIMSON_BORDER }}>
                                        <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-ink-dim hover:text-ink" data-testid="restricted-qty-dec">
                                            <Minus className="w-3.5 h-3.5" />
                                        </button>
                                        <span className="px-4 font-mono text-base w-10 text-center" data-testid="restricted-qty-value">{qty}</span>
                                        <button onClick={() => setQty(qty + 1)} className="px-3 py-2 text-ink-dim hover:text-ink" data-testid="restricted-qty-inc">
                                            <Plus className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Live savings */}
                            <div className="mt-5 flex items-center justify-between text-sm">
                                <div className="text-ink-dim">
                                    <span className="font-mono">{qty} × {formatPrice(product.price_usd)}</span>
                                    {activeTier && <span className="ml-3 overline text-[10px]" style={{ color: CRIMSON }}>{activeTier.label} · {Math.round(activeTier.pct * 100)}% OFF</span>}
                                </div>
                                <div className="text-right">
                                    {activeTier && <div className="text-xs font-mono text-ink-mute line-through">{formatPrice(subtotalUsd)}</div>}
                                    <div className="font-display text-xl font-bold text-ink" data-testid="restricted-live-total">{formatPrice(finalUsd)}</div>
                                    {activeTier && <div className="overline text-[10px]" style={{ color: CRIMSON }}>SAVE {formatPrice(discountUsd)}</div>}
                                </div>
                            </div>

                            {/* Bundle tier ladder */}
                            <div className="mt-5 grid grid-cols-3 gap-2" data-testid="restricted-bundle-tiers">
                                {BUNDLE_TIERS.map((t) => {
                                    const remaining = Math.max(0, t.qty - projectedQty);
                                    const hit = projectedQty >= t.qty;
                                    return (
                                        <div key={t.qty} className="p-2.5 border text-center"
                                            style={{ borderColor: hit ? CRIMSON : CRIMSON_BORDER, background: hit ? "rgba(200,16,46,0.08)" : "transparent" }}>
                                            <div className="overline text-[8px] tracking-[0.18em]" style={{ color: hit ? CRIMSON : "#9aa5b4" }}>{t.label}</div>
                                            <div className="font-mono text-lg font-bold mt-1" style={{ color: hit ? CRIMSON : "#e5ecf5" }}>{Math.round(t.pct * 100)}%</div>
                                            {!hit && <div className="text-[9px] font-mono text-ink-mute mt-0.5">+{remaining} TO HIT</div>}
                                            {hit && <div className="text-[9px] font-mono mt-0.5" style={{ color: CRIMSON }}>✓ UNLOCKED</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <button onClick={handleAdd} disabled={product.in_stock === false}
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-8 py-4 font-bold tracking-wider text-sm uppercase disabled:opacity-40 transition-colors"
                            style={{ background: CRIMSON, color: "#fff" }}
                            data-testid="restricted-add-to-cart">
                            {product.in_stock === false ? "OUT OF STOCK" : `ADD ${qty} TO CART · ${formatPrice(finalUsd)}`}
                        </button>

                        <Link to="/cart" className="mt-3 block text-center text-xs font-mono uppercase tracking-[0.15em] text-ink-dim hover:text-ink transition-colors" data-testid="restricted-view-cart">
                            VIEW CART →
                        </Link>

                        {/* Research-only banner */}
                        <div className="mt-8 p-4 border flex items-start gap-3" style={{ borderColor: CRIMSON_BORDER, background: "rgba(200,16,46,0.04)" }}>
                            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" style={{ color: CRIMSON }} strokeWidth={1.5} />
                            <p className="text-[10px] font-mono uppercase tracking-[0.15em] leading-relaxed" style={{ color: CRIMSON }}>
                                Strictly for laboratory and in-vitro research purposes only. Not for human or animal consumption.
                            </p>
                        </div>
                    </div>
                </div>

                {/* RESEARCH DOSSIER (magazine layout) */}
                <div className="grid md:grid-cols-[280px_1fr] gap-12 mb-20">
                    {/* Sticky quick-facts sidebar */}
                    <aside className="md:sticky md:top-24 self-start space-y-3">
                        <div className="border-l-4 pl-4" style={{ borderColor: CRIMSON }}>
                            <p className="overline text-[10px] tracking-[0.22em]" style={{ color: CRIMSON }}>/ DOSSIER</p>
                            <h2 className="font-display text-2xl font-bold text-ink mt-2 leading-tight">{product.name}</h2>
                            <p className="mt-1 text-xs font-mono text-ink-dim">{product.dosage}</p>
                        </div>

                        <div className="space-y-3 pt-3">
                            {product.ester && <QuickFact label="ESTER" value={product.ester} icon={Beaker} />}
                            {product.half_life && <QuickFact label="HALF-LIFE" value={product.half_life} icon={Clock} />}
                            {product.structure_class && <QuickFact label="STRUCTURE" value={product.structure_class} />}
                            {product.ar_affinity && <QuickFact label="AR AFFINITY" value={product.ar_affinity} />}
                            {product.aromatization && <QuickFact label="AROMATIZATION" value={product.aromatization} />}
                            {product.hepatotoxic && <QuickFact label="HEPATOTOXIC" value={product.hepatotoxic} />}
                            {product.pct_required && <QuickFact label="PCT" value={product.pct_required.split(" · ")[0]} />}
                        </div>

                        <div className="mt-6 p-4 border" style={{ borderColor: CRIMSON_BORDER, background: "rgba(200,16,46,0.04)" }}>
                            <div className="flex items-center gap-2 mb-2">
                                <FileCheck2 className="w-3.5 h-3.5" style={{ color: CRIMSON }} strokeWidth={2} />
                                <p className="overline text-[9px] tracking-[0.18em]" style={{ color: CRIMSON }}>/ THIRD-PARTY VERIFIED</p>
                            </div>
                            <p className="text-[11px] text-ink-dim leading-relaxed">Every batch independently tested by Janoshik Analytical · HPLC purity + MS identity confirmation.</p>
                            <a href={product.coa_url || "#"} target="_blank" rel="noopener noreferrer"
                                onClick={(e) => { if (product.coa_image_url || !product.coa_url || product.coa_url === "https://janoshik.com/") { e.preventDefault(); setCoaOpen(true); } }}
                                className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] hover:underline" style={{ color: CRIMSON }}>
                                VIEW COA <ExternalLink className="w-3 h-3" />
                            </a>
                        </div>
                    </aside>

                    {/* Main dossier text */}
                    <div className="space-y-10">
                        <div>
                            <p className="overline text-[11px] tracking-[0.22em]" style={{ color: CRIMSON }}>/ RESEARCH DOSSIER</p>
                            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-ink leading-tight tracking-tight">The full breakdown.</h2>
                            <p className="mt-3 text-ink-dim text-sm">Compiled from peer-reviewed pharmacokinetic, endocrinology, and veterinary research literature. For laboratory reference only.</p>
                        </div>

                        <Section num="01" label="WHAT WE KNOW">{product.what_we_know}</Section>
                        <Section num="02" label="MECHANISM · WHAT IT DOES">{product.what_it_does}</Section>
                        <Section num="03" label="COMMON RESEARCH PROTOCOL">{product.dosage_info}</Section>

                        {/* Inline dose calculator */}
                        <section className="border-t pt-10" style={{ borderColor: CRIMSON_BORDER }}>
                            <div className="flex items-baseline gap-3 mb-4">
                                <span className="font-mono text-[10px] tracking-[0.22em]" style={{ color: CRIMSON }}>/ 04</span>
                                <p className="overline text-[10px] tracking-[0.22em]" style={{ color: CRIMSON }}>VOLUMETRIC DOSE CALCULATOR</p>
                            </div>
                            <AasDoseCalc product={product} />
                        </section>

                        {product.aromatization && (
                            <Section num="05" label="AROMATIZATION & ESTROGEN PROFILE">{product.aromatization}</Section>
                        )}
                        {product.hepatotoxic && (
                            <Section num="06" label="HEPATOTOXICITY">{product.hepatotoxic}</Section>
                        )}
                        {product.side_effect_profile && (
                            <Section num="07" label="SIDE-EFFECT PROFILE">
                                <div className="flex items-start gap-3 p-4 border" style={{ borderColor: CRIMSON_BORDER, background: "rgba(200,16,46,0.04)" }}>
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: CRIMSON }} strokeWidth={2} />
                                    <p className="text-sm leading-relaxed">{product.side_effect_profile}</p>
                                </div>
                            </Section>
                        )}
                        {product.common_stacks && (
                            <Section num="08" label="COMMONLY STUDIED STACKS">{product.common_stacks}</Section>
                        )}
                        {product.pct_required && (
                            <Section num="09" label="PCT (POST-CYCLE) CONSIDERATIONS">{product.pct_required}</Section>
                        )}
                        <Section num="10" label="HOW LONG A VIAL LASTS">{product.lasts}</Section>
                    </div>
                </div>

                {/* Bottom disclaimer */}
                <div className="border-t pt-8 pb-12" style={{ borderColor: CRIMSON_BORDER }}>
                    <div className="flex items-start gap-3 max-w-3xl">
                        <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" style={{ color: CRIMSON }} strokeWidth={1.5} />
                        <p className="text-[11px] font-mono uppercase tracking-[0.15em] leading-relaxed" style={{ color: CRIMSON }}>
                            All products in the restricted catalog are sold and delivered strictly for laboratory and in-vitro research purposes. They are not for human or animal consumption, therapeutic, diagnostic, or clinical use. By purchasing you affirm you are a qualified researcher.
                        </p>
                    </div>
                </div>
            </div>

            {/* Full-screen COA lightbox — shows the uploaded Janoshik image, or a fallback message */}
            {coaOpen && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 bg-black flex flex-col"
                    style={{ zIndex: 10000 }}
                    data-testid="restricted-coa-modal"
                >
                    <div className="flex items-center justify-between px-5 py-3 border-b bg-ink-base shrink-0" style={{ borderColor: CRIMSON_BORDER }}>
                        <div className="flex items-center gap-3 min-w-0">
                            <FileCheck2 className="w-5 h-5 shrink-0" style={{ color: CRIMSON }} strokeWidth={2} />
                            <div className="min-w-0">
                                <p className="overline text-[10px] tracking-[0.2em]" style={{ color: CRIMSON }}>CERTIFICATE OF ANALYSIS · JANOSHIK LABS</p>
                                <p className="text-sm text-ink font-mono truncate">{product.name} · {product.dosage}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setCoaOpen(false)}
                            className="w-10 h-10 flex items-center justify-center text-ink-dim hover:text-ink hover:bg-ink-surface transition-colors border"
                            style={{ borderColor: CRIMSON_BORDER }}
                            data-testid="restricted-coa-close"
                            aria-label="Close"
                        >
                            <span className="block text-2xl leading-none">×</span>
                        </button>
                    </div>
                    <div
                        className="flex-1 overflow-auto bg-black flex items-center justify-center p-4"
                        onClick={() => setCoaOpen(false)}
                    >
                        {product.coa_image_url ? (
                            <img
                                src={product.coa_image_url}
                                alt={`${product.name} Janoshik COA`}
                                onClick={(e) => e.stopPropagation()}
                                className="max-w-full h-auto shadow-2xl cursor-default bg-white"
                                style={{ maxHeight: "calc(100vh - 120px)" }}
                                data-testid="restricted-coa-fullsize-image"
                            />
                        ) : (
                            <div className="text-center px-6 max-w-md" onClick={(e) => e.stopPropagation()}>
                                <ShieldCheck className="w-14 h-14 mx-auto" style={{ color: CRIMSON }} strokeWidth={1.5} />
                                <h3 className="mt-4 font-display text-xl font-bold text-ink">Certificate of Analysis</h3>
                                <p className="mt-3 text-sm text-ink-dim leading-relaxed">Batch-specific Janoshik HPLC + MS report is available on request after ordering. Message us via Telegram with your order number and we will forward the PDF within 1 hour.</p>
                                <button onClick={() => setCoaOpen(false)} className="mt-6 px-5 py-2 text-xs font-bold uppercase tracking-wider" style={{ background: CRIMSON, color: "#fff" }}>CLOSE</button>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
